/**
 * Bot Lifecycle Context Provider.
 *
 * This module provides real-time bot lifecycle state management for React components:
 * - Bot status tracking (created, starting, running, stopped, error, etc.)
 * - Real-time updates via SSE/WebSocket
 * - Subscription-based event filtering
 * - Performance metrics tracking
 *
 * @example
 * // Wrap your app with the provider
 * <BotLifecycleProvider>
 *   <App />
 * </BotLifecycleProvider>
 *
 * // Use the hook in components
 * const { bots, getBotStatus, subscribe } = useBotLifecycle();
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import sseClient from '@/lib/sseClient';

// =============================================================================
// Types
// =============================================================================

export type BotLifecycleStatus =
  | 'created'
  | 'starting'
  | 'running'
  | 'paused'
  | 'stopping'
  | 'stopped'
  | 'error'
  | 'terminated';

export interface BotState {
  id: string;
  name?: string;
  status: BotLifecycleStatus;
  previousStatus?: BotLifecycleStatus;
  lastUpdated: Date;
  lastHeartbeat?: Date;
  errorMessage?: string;
  errorCode?: string;
  containerId?: string;
  containerIp?: string;
  exchangeConnected?: boolean;
  metrics?: BotMetrics;
  tenantId?: string;
}

export interface BotMetrics {
  cpuPercent?: number;
  memoryMb?: number;
  activeOrders?: number;
  openPositions?: number;
  signalsGenerated?: number;
  ordersPlaced?: number;
  pnlRealized?: number;
  pnlUnrealized?: number;
  totalTrades?: number;
}

export interface BotStateChangeEvent {
  type: 'bot_state_change';
  botId: string;
  fromStatus?: string;
  toStatus: string;
  triggeredBy?: string;
  triggerSource?: string;
  reason?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
  tenantId?: string;
}

export interface BotHeartbeatEvent {
  type: 'bot_heartbeat';
  botId: string;
  toStatus: string;
  timestamp: string;
  metadata?: BotMetrics;
  tenantId?: string;
}

export type BotLifecycleEvent = BotStateChangeEvent | BotHeartbeatEvent;

export interface BotLifecycleContextType {
  /** Map of all tracked bots by ID */
  bots: Map<string, BotState>;

  /** Get status of a specific bot */
  getBotStatus: (botId: string) => BotState | undefined;

  /** Get all bots with a specific status */
  getBotsByStatus: (status: BotLifecycleStatus) => BotState[];

  /** Get all active (running/starting) bots */
  getActiveBots: () => BotState[];

  /** Subscribe to events for a specific bot */
  subscribeToBot: (botId: string, callback: (event: BotLifecycleEvent) => void) => () => void;

  /** Subscribe to all bot events */
  subscribeToAll: (callback: (event: BotLifecycleEvent) => void) => () => void;

  /** Update bot state manually (for optimistic updates) */
  updateBotState: (botId: string, state: Partial<BotState>) => void;

  /** Clear all tracked bots */
  clearBots: () => void;

  /** Connection status */
  isConnected: boolean;

  /** Last event timestamp */
  lastEventAt: Date | null;
}

// =============================================================================
// Context
// =============================================================================

const BotLifecycleContext = createContext<BotLifecycleContextType | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface BotLifecycleProviderProps {
  children: ReactNode;
}

export function BotLifecycleProvider({ children }: BotLifecycleProviderProps) {
  const [bots, setBots] = useState<Map<string, BotState>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [lastEventAt, setLastEventAt] = useState<Date | null>(null);

  // Subscription refs
  const botSubscribersRef = useRef<Map<string, Set<(event: BotLifecycleEvent) => void>>>(
    new Map()
  );
  const allSubscribersRef = useRef<Set<(event: BotLifecycleEvent) => void>>(new Set());

  // Handle bot state change events
  const handleStateChange = useCallback((data: unknown) => {
    const event = data as BotStateChangeEvent;
    if (!event.botId) return;

    setLastEventAt(new Date());

    setBots((prev) => {
      const newBots = new Map(prev);
      const existing = newBots.get(event.botId);

      const updatedBot: BotState = {
        id: event.botId,
        name: existing?.name,
        status: event.toStatus as BotLifecycleStatus,
        previousStatus: (event.fromStatus || existing?.status) as BotLifecycleStatus | undefined,
        lastUpdated: new Date(event.timestamp),
        lastHeartbeat: existing?.lastHeartbeat,
        errorMessage: event.metadata?.errorMessage as string | undefined,
        errorCode: event.metadata?.errorCode as string | undefined,
        containerId: existing?.containerId,
        containerIp: existing?.containerIp,
        exchangeConnected: existing?.exchangeConnected,
        metrics: existing?.metrics,
        tenantId: event.tenantId || existing?.tenantId,
      };

      newBots.set(event.botId, updatedBot);
      return newBots;
    });

    // Notify subscribers
    notifySubscribers(event);
  }, []);

  // Handle bot heartbeat events
  const handleHeartbeat = useCallback((data: unknown) => {
    const event = data as BotHeartbeatEvent;
    if (!event.botId) return;

    setLastEventAt(new Date());

    setBots((prev) => {
      const newBots = new Map(prev);
      const existing = newBots.get(event.botId);

      const updatedBot: BotState = {
        id: event.botId,
        name: existing?.name,
        status: (event.toStatus || existing?.status || 'running') as BotLifecycleStatus,
        previousStatus: existing?.previousStatus,
        lastUpdated: new Date(event.timestamp),
        lastHeartbeat: new Date(event.timestamp),
        errorMessage: existing?.errorMessage,
        errorCode: existing?.errorCode,
        containerId: existing?.containerId,
        containerIp: existing?.containerIp,
        exchangeConnected: existing?.exchangeConnected,
        metrics: event.metadata || existing?.metrics,
        tenantId: event.tenantId || existing?.tenantId,
      };

      newBots.set(event.botId, updatedBot);
      return newBots;
    });

    // Notify subscribers
    notifySubscribers(event);
  }, []);

  // Handle connection status
  const handleConnected = useCallback(() => {
    setIsConnected(true);
  }, []);

  const handleHeartbeatPing = useCallback(() => {
    // Just update connection status on heartbeat
    setIsConnected(true);
  }, []);

  // Notify subscribers of an event
  const notifySubscribers = (event: BotLifecycleEvent) => {
    // Notify bot-specific subscribers
    const botSubs = botSubscribersRef.current.get(event.botId);
    if (botSubs) {
      botSubs.forEach((callback) => {
        try {
          callback(event);
        } catch (err) {
          console.error('Bot subscriber error:', err);
        }
      });
    }

    // Notify all-events subscribers
    allSubscribersRef.current.forEach((callback) => {
      try {
        callback(event);
      } catch (err) {
        console.error('All-events subscriber error:', err);
      }
    });
  };

  // Set up SSE event listeners
  useEffect(() => {
    // Listen for bot status events from the existing SSE stream
    sseClient.on('botStatus', handleStateChange);
    sseClient.on('bot_state_change', handleStateChange);
    sseClient.on('bot_heartbeat', handleHeartbeat);
    sseClient.on('connected', handleConnected);
    sseClient.on('heartbeat', handleHeartbeatPing);

    return () => {
      sseClient.off('botStatus', handleStateChange);
      sseClient.off('bot_state_change', handleStateChange);
      sseClient.off('bot_heartbeat', handleHeartbeat);
      sseClient.off('connected', handleConnected);
      sseClient.off('heartbeat', handleHeartbeatPing);
    };
  }, [handleStateChange, handleHeartbeat, handleConnected, handleHeartbeatPing]);

  // =============================================================================
  // Context Methods
  // =============================================================================

  const getBotStatus = useCallback(
    (botId: string): BotState | undefined => {
      return bots.get(botId);
    },
    [bots]
  );

  const getBotsByStatus = useCallback(
    (status: BotLifecycleStatus): BotState[] => {
      return Array.from(bots.values()).filter((bot) => bot.status === status);
    },
    [bots]
  );

  const getActiveBots = useCallback((): BotState[] => {
    return Array.from(bots.values()).filter((bot) =>
      ['running', 'starting', 'paused'].includes(bot.status)
    );
  }, [bots]);

  const subscribeToBot = useCallback(
    (botId: string, callback: (event: BotLifecycleEvent) => void): (() => void) => {
      if (!botSubscribersRef.current.has(botId)) {
        botSubscribersRef.current.set(botId, new Set());
      }
      botSubscribersRef.current.get(botId)!.add(callback);

      // Return unsubscribe function
      return () => {
        const subs = botSubscribersRef.current.get(botId);
        if (subs) {
          subs.delete(callback);
          if (subs.size === 0) {
            botSubscribersRef.current.delete(botId);
          }
        }
      };
    },
    []
  );

  const subscribeToAll = useCallback(
    (callback: (event: BotLifecycleEvent) => void): (() => void) => {
      allSubscribersRef.current.add(callback);

      // Return unsubscribe function
      return () => {
        allSubscribersRef.current.delete(callback);
      };
    },
    []
  );

  const updateBotState = useCallback((botId: string, state: Partial<BotState>) => {
    setBots((prev) => {
      const newBots = new Map(prev);
      const existing = newBots.get(botId);

      if (existing) {
        newBots.set(botId, { ...existing, ...state, lastUpdated: new Date() });
      } else {
        newBots.set(botId, {
          id: botId,
          status: 'created',
          lastUpdated: new Date(),
          ...state,
        } as BotState);
      }

      return newBots;
    });
  }, []);

  const clearBots = useCallback(() => {
    setBots(new Map());
  }, []);

  // =============================================================================
  // Context Value
  // =============================================================================

  const contextValue: BotLifecycleContextType = {
    bots,
    getBotStatus,
    getBotsByStatus,
    getActiveBots,
    subscribeToBot,
    subscribeToAll,
    updateBotState,
    clearBots,
    isConnected,
    lastEventAt,
  };

  return (
    <BotLifecycleContext.Provider value={contextValue}>{children}</BotLifecycleContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useBotLifecycle(): BotLifecycleContextType {
  const context = useContext(BotLifecycleContext);
  if (!context) {
    throw new Error('useBotLifecycle must be used within BotLifecycleProvider');
  }
  return context;
}

// =============================================================================
// Specialized Hooks
// =============================================================================

/**
 * Hook to track a specific bot's status.
 *
 * @param botId - The bot ID to track
 * @returns The bot's current state or undefined if not found
 *
 * @example
 * const botState = useBotStatus('bot-123');
 * if (botState?.status === 'running') {
 *   // Bot is running
 * }
 */
export function useBotStatus(botId: string): BotState | undefined {
  const { getBotStatus, subscribeToBot } = useBotLifecycle();
  const [botState, setBotState] = useState<BotState | undefined>(() => getBotStatus(botId));

  useEffect(() => {
    // Get initial state
    setBotState(getBotStatus(botId));

    // Subscribe to updates
    const unsubscribe = subscribeToBot(botId, () => {
      setBotState(getBotStatus(botId));
    });

    return unsubscribe;
  }, [botId, getBotStatus, subscribeToBot]);

  return botState;
}

/**
 * Hook to get all active bots.
 *
 * @returns Array of active bot states
 *
 * @example
 * const activeBots = useActiveBots();
 * console.log(`${activeBots.length} bots are currently active`);
 */
export function useActiveBots(): BotState[] {
  const { getActiveBots, subscribeToAll } = useBotLifecycle();
  const [activeBots, setActiveBots] = useState<BotState[]>(() => getActiveBots());

  useEffect(() => {
    // Get initial state
    setActiveBots(getActiveBots());

    // Subscribe to all updates
    const unsubscribe = subscribeToAll(() => {
      setActiveBots(getActiveBots());
    });

    return unsubscribe;
  }, [getActiveBots, subscribeToAll]);

  return activeBots;
}

/**
 * Hook to subscribe to bot lifecycle events.
 *
 * @param botId - Optional bot ID to filter events (undefined for all events)
 * @param callback - Callback function to invoke on each event
 *
 * @example
 * useBotLifecycleEvents('bot-123', (event) => {
 *   console.log('Bot event:', event);
 * });
 */
export function useBotLifecycleEvents(
  botId: string | undefined,
  callback: (event: BotLifecycleEvent) => void
): void {
  const { subscribeToBot, subscribeToAll } = useBotLifecycle();

  useEffect(() => {
    const unsubscribe = botId ? subscribeToBot(botId, callback) : subscribeToAll(callback);

    return unsubscribe;
  }, [botId, callback, subscribeToBot, subscribeToAll]);
}

/**
 * Hook to get bots filtered by status.
 *
 * @param status - The status to filter by
 * @returns Array of bots with the specified status
 *
 * @example
 * const runningBots = useBotsByStatus('running');
 * const errorBots = useBotsByStatus('error');
 */
export function useBotsByStatus(status: BotLifecycleStatus): BotState[] {
  const { getBotsByStatus, subscribeToAll } = useBotLifecycle();
  const [filteredBots, setFilteredBots] = useState<BotState[]>(() => getBotsByStatus(status));

  useEffect(() => {
    // Get initial state
    setFilteredBots(getBotsByStatus(status));

    // Subscribe to all updates
    const unsubscribe = subscribeToAll(() => {
      setFilteredBots(getBotsByStatus(status));
    });

    return unsubscribe;
  }, [status, getBotsByStatus, subscribeToAll]);

  return filteredBots;
}

export default BotLifecycleContext;
